import { useState } from "react";

import { ChevronDownIcon } from "@heroicons/react/24/outline";

import usePartDetails from "../../../data/usePartDetails";
import { IPart } from "../../../models/Part";
import classNames from "../../../utils/classNames";
import LoadingPulse from "../../shared/LoadingPulse";
import EbayListingTitle from "../EbayListingTitle";
import QuickAiTitleButton from "../QuickAiTitleButton";

type Props = {
  part: IPart;
  /** Override the title shown in the inline editor (e.g. staged title). */
  title?: string;
  /** When set, Save / blur / AI stage via this callback instead of writing the live offer. */
  onSaveTitle?: (title: string) => void;
};

export default function UnlistedEbayTitle({ part, title, onSaveTitle }: Props) {
  const [titleBuilderOpen, setTitleBuilderOpen] = useState(false);

  const { partDetails, isLoading: detailsLoading } = usePartDetails(
    part.id,
    titleBuilderOpen
  );

  const fullPart = partDetails ?? part;
  const staging = !!onSaveTitle;

  return (
    <div
      className="flex min-w-0 flex-col border-l-2 border-sky-400 bg-gradient-to-r from-sky-50/80 via-sky-50/40 to-sky-50/80 px-3 py-2"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span className="hidden shrink-0 text-[10px] font-semibold uppercase tracking-wide text-sky-700 sm:inline">
          {staging ? "eBay Title (staged)" : "eBay Title"}
        </span>
        <div className="min-w-0 flex-1">
          <EbayListingTitle
            key={`${part.id}-${title ?? "live"}`}
            part={part}
            title={title}
            onlyTitle
            onTable
            persistTitle={!staging}
            onSaveTitle={onSaveTitle}
          />
        </div>
        <QuickAiTitleButton
          part={part}
          disabled={part.soldDate != null}
          onSaveTitle={onSaveTitle}
        />
        <button
          type="button"
          onClick={() => setTitleBuilderOpen((previous) => !previous)}
          aria-expanded={titleBuilderOpen}
          aria-label={
            titleBuilderOpen
              ? "Hide title builder (merge tiles and highlights)"
              : "Show title builder (merge tiles and highlights)"
          }
          className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-md border border-sky-200 bg-white text-sky-700 shadow-sm transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-800"
        >
          <ChevronDownIcon
            className={classNames(
              "h-4 w-4 transition-transform",
              titleBuilderOpen && "rotate-180"
            )}
            aria-hidden
          />
        </button>
      </div>

      {titleBuilderOpen && (
        <div className="mt-2 border-t border-sky-200/70 pt-2">
          {detailsLoading && !partDetails ? (
            <div className="py-3">
              <LoadingPulse />
            </div>
          ) : (
            <div className="rounded-lg border border-sky-200/70 bg-white/70 p-3 shadow-sm">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-sky-700">
                {staging ? "eBay listing template (staged)" : "eBay listing template"}
              </p>
              <EbayListingTitle
                key={`${fullPart.id}-${title ?? "live"}-builder`}
                part={fullPart}
                title={title}
                onTable
                editDefault
                isTemplate={false}
                persistTitle={!staging}
                onSaveTitle={onSaveTitle}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
